import React, {useState, useEffect, useCallback} from 'react';
import '../styles/website-form.css';

const MFACodeModal = ({isOpen, onClose, initialCampaignId = '', verify = false}) => {
    const [formData, setFormData] = useState({
        campaignId: '',
        mfaCode: ''
    });
    const [formErrors, setFormErrors] = useState({});
    const [formTouched, setFormTouched] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [statusMessage, setStatusMessage] = useState('');
    const [retryCount, setRetryCount] = useState(0);

    // Helper function to validate campaign ID format
    const isValidCampaignId = useCallback((campaignId) => {
        const campaignPattern = /^campaign_[a-z0-9_]+_\d{10}\/?$/;
        return campaignPattern.test(campaignId);
    }, []);

    // Enhanced validation with detailed feedback
    const getDetailedCampaignIdError = useCallback((campaignId) => {
        if (!campaignId.trim()) {
            return "Campaign ID is required";
        }

        const trimmedId = campaignId.trim();

        if (!trimmedId.startsWith('campaign_')) {
            return "Campaign ID must start with 'campaign_'";
        }

        const cleanId = trimmedId.replace(/\/$/, '');
        const parts = cleanId.split('_');

        if (parts.length < 3) {
            return "Campaign ID format is invalid. Expected: campaign_name_timestamp";
        }

        if (parts[0] !== 'campaign') {
            return "Campaign ID must start with 'campaign_'";
        }

        const timestamp = parts[parts.length - 1];
        if (!/^\d{10}$/.test(timestamp)) {
            return "Campaign ID must end with a 10-digit timestamp";
        }

        const nameParts = parts.slice(1, -1);
        const campaignName = nameParts.join('_');

        if (!/^[a-z0-9_]+$/.test(campaignName)) {
            return "Campaign name must contain only lowercase letters, numbers, and underscores";
        }

        if (campaignName.length === 0) {
            return "Campaign name cannot be empty";
        }

        return null;
    }, []);

    // Effect to reset form data when the modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setFormData({
                campaignId: initialCampaignId,
                mfaCode: ''
            });
            setFormErrors({});
            setFormTouched({});
            setIsSubmitting(false);
            setSubmitStatus(null);
            setStatusMessage('');
            setRetryCount(0);
        }
    }, [isOpen, initialCampaignId]);

    // Close modal on escape key press
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    // Auto-focus management
    useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => {
                if (initialCampaignId) {
                    const mfaInput = document.getElementById('mfaCode');
                    if (mfaInput) mfaInput.focus();
                } else {
                    const campaignInput = document.getElementById('campaignId');
                    if (campaignInput) campaignInput.focus();
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [isOpen, initialCampaignId]);

    const validateForm = useCallback(() => {
        const errors = {};

        const campaignError = getDetailedCampaignIdError(formData.campaignId);
        if (campaignError) {
            errors.campaignId = campaignError;
        }

        if (!formData.mfaCode.trim()) {
            errors.mfaCode = 'MFA code is required';
        } else if (!/^\d{6}$/.test(formData.mfaCode.replace(/\s/g, ''))) {
            errors.mfaCode = 'MFA code must be 6 digits';
        }

        return errors;
    }, [formData, getDetailedCampaignIdError]);

    const validateField = useCallback((fieldName, value) => {
        switch (fieldName) {
            case 'campaignId':
                return getDetailedCampaignIdError(value);
            case 'mfaCode':
                if (!value.trim()) return "MFA code is required";
                const cleanCode = value.replace(/\s/g, '');
                if (!/^\d{6}$/.test(cleanCode)) return "MFA code must be 6 digits";
                return null;
            default:
                return null;
        }
    }, [getDetailedCampaignIdError]);

    useEffect(() => {
        setFormErrors(validateForm());
    }, [formData, validateForm]);

    const handleChange = useCallback((e) => {
        const {name, value} = e.target;

        let formattedValue = value;

        if (name === 'campaignId') {
            // Convert to lowercase and allow only valid characters
            formattedValue = value.toLowerCase().replace(/[^a-z0-9_/]/g, '');
        } else if (name === 'mfaCode') {
            // Format MFA code input (add spaces for readability)
            const digits = value.replace(/\D/g, '').slice(0, 6);
            formattedValue = digits.replace(/(\d{3})(\d{1,3})/, '$1 $2');
        }

        setFormData(prev => ({...prev, [name]: formattedValue}));

        // Clear field error when user starts typing
        if (formErrors[name]) {
            setFormErrors(prev => ({...prev, [name]: null}));
        }
    }, [formErrors]);

    const handleBlur = useCallback((e) => {
        const {name, value} = e.target;
        setFormTouched(prev => ({...prev, [name]: true}));

        const fieldError = validateField(name, value);
        if (fieldError) {
            setFormErrors(prev => ({...prev, [name]: fieldError}));
        }
    }, [validateField]);

    const handleKeyDown = useCallback((e) => {
        const {name} = e.target;

        if (name === 'mfaCode') {
            if (!/[0-9]/.test(e.key) &&
                !['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                e.preventDefault();
            }
        }

        if (e.key === 'Enter' && !isSubmitting) {
            handleSubmit(e);
        }
    }, [isSubmitting]);

    // If the modal is not open, don't render anything
    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();

        const errors = validateForm();

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            setFormTouched({campaignId: true, mfaCode: true});
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);
        setStatusMessage('');

        try {
            const cleanMfaCode = formData.mfaCode.replace(/\s/g, '');
            const cleanCampaignId = formData.campaignId.trim();

            const url = new URL('https://27yg716w8g.execute-api.eu-west-1.amazonaws.com/prod/intellommerce/stripe/checkout/success');
            url.searchParams.append('campaign_id', cleanCampaignId);
            url.searchParams.append('otp', cleanMfaCode);
            url.searchParams.append('mfa_verify', verify);

            const response = await fetch(url, {
                method: 'GET', // Changed from POST to GET
                headers: {
                    // 'Content-Type' header is typically not used for GET requests
                    // as they usually have no body.
                    // You can remove it or keep it if the server expects it.
                    // 'Content-Type': 'application/json',
                },
                // body: JSON.stringify({ ... }) // REMOVE THE BODY ENTIRELY
            });

            if (response.ok) {
                const body = await response.json();

                if (body.redirect_url) {
                    console.log('Redirecting')
                    setSubmitStatus('redirect');
                    setStatusMessage('Redirecting to secure area...');

                    const redirectUrl = body.redirect_url;

                    setTimeout(() => {
                        window.location.href = redirectUrl;
                    }, 1500);

                } else{
                    console.log('MFA set up successfully.');
                    setSubmitStatus('success');
                setStatusMessage('MFA has been set up successfully! 🎉');

                setFormData({campaignId: '', mfaCode: ''});
                setFormTouched({});
            }

                return;
            }

            const errorData = await response.json().catch(() => ({}));

            if (response.status === 429) {
                setSubmitStatus('error');
                setStatusMessage('Too many attempts. Please try again later.');
                setRetryCount(prev => prev + 1);
            } else if (response.status === 400) {
                setSubmitStatus('error');
                if (errorData.field === 'campaignId') {
                    setStatusMessage('Invalid Campaign ID. Please check the format and try again.');
                    setFormErrors({campaignId: 'Campaign ID not found or invalid format'});
                } else {
                    setStatusMessage('Invalid MFA code. Please check and try again.');
                    setFormErrors({mfaCode: 'Invalid code'});
                    setFormData(prev => ({...prev, mfaCode: ''}));
                }
            } else if (response.status === 401) {
                setSubmitStatus('error');
                setStatusMessage('Session expired. Please refresh the page and try again.');
            } else {
                setSubmitStatus('error');
                setStatusMessage(errorData.message || 'An error occurred. Please try again.');
            }

        } catch (error) {
            console.error('MFA verification error:', error);
            setSubmitStatus('error');

            if (error.name === 'TypeError' && error.message.includes('fetch')) {
                setStatusMessage('Network error. Please check your connection and try again.');
            } else {
                setStatusMessage('An unexpected error occurred. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const getStatusMessageClass = () => {
        switch (submitStatus) {
            case 'success':
                return 'website-form-status-message success';
            case 'redirect':
                return 'website-form-status-message success';
            case 'error':
                return 'website-form-status-message error';
            default:
                return 'website-form-status-message';
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content mfa-modal" onClick={e => e.stopPropagation()}>
                <button className="modal-close-button" onClick={onClose} disabled={isSubmitting}>
                    &times;
                </button>

                <form className="website-form mfa-form" onSubmit={handleSubmit}>
                    <div className="mfa-modal-header">
                        <h2>🔐 <span className="highlight">Multi-Factor Authentication</span></h2>
                        <h3>Verify Your Campaign & Code</h3>
                        <p>Please enter your Campaign ID and the 6-digit verification code from your authenticator app
                            or SMS.</p>
                    </div>

                    {statusMessage && (
                        <div className={getStatusMessageClass()}>
                            {submitStatus === 'success' && <span className="status-icon">✅</span>}
                            {submitStatus === 'redirect' && <span className="status-icon">↗️</span>}
                            {submitStatus === 'error' && <span className="status-icon">❌</span>}
                            {statusMessage}
                        </div>
                    )}

                    <div className="website-form-group">
                        <label htmlFor="campaignId">Campaign ID *</label>
                        <input
                            type="text"
                            id="campaignId"
                            name="campaignId"
                            value={formData.campaignId}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="campaign_example_name_1234567890"
                            className={`campaign-input ${(formTouched.campaignId && formErrors.campaignId) ? 'error' : ''}`}
                            disabled={isSubmitting}
                            autoComplete="username"
                        />
                        {formTouched.campaignId && formErrors.campaignId && (
                            <span className="website-form-error-message">{formErrors.campaignId}</span>
                        )}
                        <small className="input-hint">
                            Format: campaign_name_timestamp (e.g., campaign_pishon_cakes_1755869471)
                        </small>
                    </div>

                    <div className="website-form-group mfa-input-group">
                        <label htmlFor="mfaCode">Verification Code *</label>
                        <input
                            type="text"
                            id="mfaCode"
                            name="mfaCode"
                            value={formData.mfaCode}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onKeyDown={handleKeyDown}
                            placeholder="000 000"
                            maxLength={7}
                            className={`mfa-code-input ${(formTouched.mfaCode && formErrors.mfaCode) ? 'error' : ''}`}
                            disabled={isSubmitting}
                            autoComplete="one-time-code"
                        />
                        {formTouched.mfaCode && formErrors.mfaCode && (
                            <span className="website-form-error-message">{formErrors.mfaCode}</span>
                        )}
                        <small className="input-hint">
                            Enter the 6-digit code exactly as shown in your authenticator app
                        </small>
                    </div>

                    <div className="hero-cta mfa-actions">
                        <button
                            type="submit"
                            className={`primary-button submit-button ${isSubmitting ? 'submitting' : ''} ${submitStatus === 'success' ? 'success' : ''} ${submitStatus === 'redirect' ? 'redirect' : ''}`}
                            disabled={isSubmitting || submitStatus === 'success' || submitStatus === 'redirect'}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner"></span>
                                    Verifying...
                                </>
                            ) : submitStatus === 'success' ? (
                                '✅ Verified!'
                            ) : submitStatus === 'redirect' ? (
                                '↗️ Redirecting...'
                            ) : (
                                '🔐 Verify Code'
                            )}
                        </button>

                        {retryCount < 3 && submitStatus !== 'success' && submitStatus !== 'redirect' && (
                            <button
                                type="button"
                                className="secondary-button"
                                onClick={onClose}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                        )}
                    </div>

                    {retryCount > 0 && retryCount < 3 && (
                        <p className="retry-info">
                            Attempts remaining: {3 - retryCount}
                        </p>
                    )}

                    <p className="website-form-disclaimer mfa-disclaimer">
                        Having trouble? <a href="?modal=mfa-help">Get help with MFA setup</a>
                    </p>
                </form>
            </div>
        </div>
    );
};

export default MFACodeModal;